import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import moment from "moment";
import uuidv4 from 'uuid/v4';
import API from "../../app/api";
import config from "../../app/config";
import { setBedasList, setSingleBedasSessions, setUpdating } from "./ward";
import { setSnackbar } from "./globals";
import {
  changeBulkNewSlotKey,
  createBarsSessionObject,
  createMultipleSessions,
  getWardDataAppointmentsKey,
  prepareSessionsForCalendarObject,
} from "../../pages/ward/map/patientList/components/edReview/utils/sessionUtilities";
import {
  ED_REVIEW_MODALS,
  filterReviewsByDayAndSessionId,
  getPatientsReviewStatus,
  getPreparedReviewObject,
  getReviewData,
  SLOT_MANIPULATION_OPTIONS,
} from "../../pages/ward/map/patientList/utils/edReviewUtils";
import { setLoading } from "./patient";

export const INIT_CACHE_OBJ = { regular: {}, single: {} };

export const INIT_FILTER_STATE = {
  startDate: moment(),
  endDate: null,
  clinician: null,
  status: null,
  healthcareService: null,
  specialty: null,
  consultantRole: null,
  location: []
};

const INIT_SLOT_MANIPULATION_STATE = {
  isLocking: false,
  isCreating: false,
  isAssigning: false,
  temporaryEvents: {},
  availableOptions: {},
  dayCalendarColumns: [],
};

const INIT_ED_REVIEW_STATE = {
  reviewList: [], // List of patients to review
  hookSlots: {},
  sessionIds: {},
  selectedMap: "",
  availableSlots: {}, // obj of days in month -> key = day of month -> { sessionId : number of av. slots }
  editedSession: null,
  selectedPatient: null,
  cancelledReviews: [],
  selectedWardId: "ED",
  filterObject: INIT_FILTER_STATE,
  // Cache for session data
  // (e.g. iterating through sessionIds and fetching from here sess data, else pushing and retrieve from wardSlice that session)
  sessionCache: INIT_CACHE_OBJ,
  slotManipulationState: INIT_SLOT_MANIPULATION_STATE,
};

const INIT_BARS_REVIEW_STATE = {
  reviewList: [], // List of patients to review
  sessions: [], // List of patients to review
  hookSlots: {},
  sessionIds: {},
  availableSlots: {}, // obj of days in month -> key = day of month -> { sessionId : number of av. slots }
  editedSession: null,
  selectedPatient: null,
  cancelledReviews: [],
  filterObject: INIT_FILTER_STATE,
  sessionCache: INIT_CACHE_OBJ,
  slotManipulationState: INIT_SLOT_MANIPULATION_STATE,
};

const REVIEW_MODAL_STATE = {
  addEditEdSession: false,
  changeReviewStatus: false,
  assignSession: false,
  modalHelperState: null,
};

// Possibly to reuse in the future due to other kind of events/sessions/reviews
export const KIND_OF_REVIEW_STATE = {
  BARS: "bars",
  ED_REVIEW: "edReview",
};

const isValidContextType = (ctx) =>
  ctx === KIND_OF_REVIEW_STATE.ED_REVIEW || ctx === KIND_OF_REVIEW_STATE.BARS;

export const reviewSlice = createSlice({
  name: "reviews",
  initialState: {
    [KIND_OF_REVIEW_STATE.ED_REVIEW]: INIT_ED_REVIEW_STATE,
    [KIND_OF_REVIEW_STATE.BARS]: INIT_BARS_REVIEW_STATE,
    activeState: KIND_OF_REVIEW_STATE.ED_REVIEW,
    selectedMap: "",
    selectedWardId: "",
    dischargeContext: false,
    calendarMonth: moment(),
    dailyCalendarOpen: false,
    modalState: REVIEW_MODAL_STATE,
    selectedDate: moment().format("DD/MM/YYYY"),
    consultantFilterOptions: [],
    healthcareServiceFilterOptions: [],
    specialtyFilterOptions: [],
    consultantRoleFilterOptions: [],
  },
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setCalendarMonth: (state, action) => {
      state.calendarMonth = action.payload;
    },
    setDischargeContext: (state, action) => {
      state.dischargeContext = !!action.payload;
    },
    setIsDailyCalendarOpen: (state, action) => {
      state.dailyCalendarOpen = !!action.payload;
    },
    setConsultantFilterOptions: (state, action) => {
      state.consultantFilterOptions = action.payload;
    },
    setHealthcareServiceFilterOptions: (state, action) => {
      state.healthcareServiceFilterOptions = action.payload;
    },
    setConsultantRoleFilterOptions: (state, action) => {
      state.consultantRoleFilterOptions = action.payload;
    },
    setSpecialtyFilterOptions: (state, action) => {
      state.specialtyFilterOptions = action.payload;
    },
    setSelectedMap: (state, maps) => {
      state.selectedMap = maps.payload.selectedMap;
      state.selectedWardId = maps.payload.selectedWardId;
    },
    setActiveState: (state, activeContext) => {
      if (
        activeContext.payload === KIND_OF_REVIEW_STATE.ED_REVIEW ||
        activeContext.payload === KIND_OF_REVIEW_STATE.BARS
      ) {
        state.activeState = activeContext.payload;
      }
    },
    setEdReview: (state, edReview) => {
      state[KIND_OF_REVIEW_STATE.ED_REVIEW] = {
        ...INIT_ED_REVIEW_STATE,
        ...state[KIND_OF_REVIEW_STATE.ED_REVIEW],
        ...edReview.payload,
      };
    },
    setBarsReview: (state, bars) => {
      state[KIND_OF_REVIEW_STATE.BARS] = {
        ...INIT_BARS_REVIEW_STATE,
        ...state[KIND_OF_REVIEW_STATE.BARS],
        ...bars.payload,
      };
    },
    setModalState: (state, modal) => {
      state.modalState = {
        ...state.modalState,
        ...modal.payload,
      };
    },
    setModalHelperState: (state, modal) => {
      state.modalState = {
        ...state.modalState,
        modalHelperState: modal.payload,
      };
    },
    setSessionIds: (state, action) => {
      const ctxType = isValidContextType(action.payload?.contextType)
        ? action.payload?.contextType
        : KIND_OF_REVIEW_STATE.ED_REVIEW;
      let clone = _.cloneDeep(state[ctxType]);

      if (_.isEmpty(Object.keys(state?.[ctxType]?.sessionIds))) {
        state[ctxType].sessionIds = action.payload.sessionIds;
      } else {
        state[ctxType].sessionIds = {
          ...clone.sessionIds,
          ...action.payload.sessionIds,
        };
      }
    },
    setSessionCache: (state, action) => {
      // {type: 'regular | single', sessionId, data => session, contextType: 'bars | edReview'}
      const ctxType = isValidContextType(action.payload?.contextType)
        ? action.payload?.contextType
        : KIND_OF_REVIEW_STATE.ED_REVIEW;
      let clone = _.cloneDeep(state[ctxType]);

      state[ctxType].sessionCache[action.payload.type] = {
        ...clone.sessionCache[action.payload.type],
        [action.payload.sessionId]: action.payload.data,
      };
    },
    setBarsReviews: (state, action) => {
      if (_.isArray(action.payload)) {
        state[KIND_OF_REVIEW_STATE.BARS].reviewList = action.payload;
      } else {
        console.error("BaRS Reviews payload has to be array type.");
      }
    },
  },
});

export const barsReview = (state) => state.reviews[KIND_OF_REVIEW_STATE.BARS];
export const edReview = (state) =>
  state.reviews[KIND_OF_REVIEW_STATE.ED_REVIEW];
export const sessionIds = (state) =>
  state.reviews[state.reviews.activeState].sessionIds;
export const slotsFromHook = (state) =>
  state.reviews[state.reviews.activeState].hookSlots;
export const reviewListSelector = (state) =>
  state.reviews[state.reviews.activeState].reviewList;
export const reviewFilters = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.filterObject;
export const selectedPatient = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.selectedPatient;
export const cancelledReviewList = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.cancelledReviews;

export const selectedMap = (state) => state.reviews.selectedMap;
export const selectedDate = (state) => state.reviews.selectedDate;
export const calendarMonth = (state) => state.reviews.calendarMonth;
export const selectedWardId = (state) => state.reviews.selectedWardId;
export const isDayCalendarOpen = (state) => state.reviews.dailyCalendarOpen;

// Manip state selectors
export const slotManipulationState = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState;
export const isLockingSlot = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState.isLocking;
export const isCreatingSlot = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .isCreating;
export const isAssigningSlot = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .isAssigning;
export const isModifying = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .isLocking ||
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .isCreating ||
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .isAssigning;

export const temporaryEvents = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .temporaryEvents;
export const availableOptions = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .availableOptions;
export const dayCalendarColumns = (state) =>
  state.reviews?.[state?.reviews?.activeState]?.slotManipulationState
    .dayCalendarColumns;

// Modals
export const isAssignSessionModalOpen = (state) =>
  state.reviews.modalState.assignSession;
export const reviewModalHelperState = (state) =>
  state.reviews.modalState.modalHelperState;
export const isAddNewEdSessionModalOpen = (state) =>
  state.reviews.modalState.addEditEdSession;
export const isChangeReviewStatusModalOpen = (state) =>
  state.reviews.modalState.changeReviewStatus;

export const activeContext = (state) => state.reviews.activeState;
export const isDischargeContext = (state) => state.reviews.dischargeContext;
export const barsContextActive = (state) =>
  state.reviews.activeState === KIND_OF_REVIEW_STATE.BARS;
export const allBarsSessions = (state) =>
  state.reviews?.[KIND_OF_REVIEW_STATE.BARS]?.sessions;
export const allBarsReviews = (state) =>
  state.reviews?.[KIND_OF_REVIEW_STATE.BARS]?.reviewList;

// Filter options
export const specialtyFilterOptions = (state) =>
  state.reviews?.specialtyFilterOptions;
export const consultantFilterOptions = (state) =>
  state.reviews?.consultantFilterOptions;
export const consultantRoleFilterOptions = (state) =>
  state.reviews?.consultantRoleFilterOptions;
export const healthcareServiceFilterOptions = (state) =>
  state.reviews?.healthcareServiceFilterOptions;

export const {
  setEdReview,
  setSessionIds,
  setModalState,
  setBarsReview,
  setSelectedMap,
  setActiveState,
  setBarsReviews,
  setSessionCache,
  setSelectedDate,
  setCalendarMonth,
  setDischargeContext,
  setModalHelperState,
  setIsDailyCalendarOpen,
  setSpecialtyFilterOptions,
  setConsultantFilterOptions,
  setConsultantRoleFilterOptions,
  setHealthcareServiceFilterOptions,
} = reviewSlice.actions;

export const toggleActiveContext = () => (dispatch, getState) => {
  const currentContext = getState().reviews.activeState;

  dispatch(
    setActiveState(
      currentContext === KIND_OF_REVIEW_STATE.ED_REVIEW
        ? KIND_OF_REVIEW_STATE.BARS
        : KIND_OF_REVIEW_STATE.ED_REVIEW,
    ),
  );
};

const getStateSetter = (isBars) => (!isBars ? setEdReview : setBarsReview);

export const setReviewList =
  (list, setCancelled = false, sortResults = true) =>
  (dispatch, getState) => {
    let sorted = _.cloneDeep(list);
    const changedKey = setCancelled ? "cancelledReviews" : "reviewList";
    const edReviewStateClone = _.cloneDeep(
      getState().reviews[KIND_OF_REVIEW_STATE.ED_REVIEW],
    );

    if (sortResults) {
      sorted = _.sortBy(list, (pt) => moment(pt?.bedas?.date));
    }

    // const barsReviews = _.filter(sorted, pt => !!getReviewData(pt)?.barsAppointment);
    const ordinaryReviews = _.filter(
      sorted,
      (pt) => !getReviewData(pt)?.barsAppointment,
    );
    //
    // dispatch(setBarsReview({
    //   ...barsStateClone,
    //   [changedKey]: barsReviews || []
    // }));

    dispatch(
      setEdReview({
        ...edReviewStateClone,
        [changedKey]: ordinaryReviews || [],
      }),
    );
  };

export const handleResetReviewsState = (dispatch, getState) => {
  const currentContext = getState().reviews.activeState;
  const clone = _.cloneDeep(getState().reviews?.[currentContext]);
  const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);

  const toChange = {
    ...clone,
    reviewList: [],
    sessionIds: {},
    editedSession: null,
    selectedPatient: null,
    cancelledReviews: [],
    dailyCalendarDate: null,
    dailyCalendarOpen: false,
    filterObject: INIT_FILTER_STATE,
    sessionCache: INIT_CACHE_OBJ,
    selectedDate: moment().format("DD/MM/YYYY"),
    slotManipulationState: INIT_SLOT_MANIPULATION_STATE,
  };

  dispatch(setter({ ...toChange }));
};

export const setSlotManipulationState =
  ({
    option,
    eventList = null,
    discard = false,
    availableOptions,
    reason = "",
    dayCalendarColumns,
    sessionId,
    colIdx,
    key,
    value,
  }) =>
  (dispatch, getState) => {
    const currentContext = getState().reviews.activeState;
    const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);
    let clone = _.cloneDeep(
      getState().reviews[currentContext].slotManipulationState,
    );

    if (discard) {
      return dispatch(
        setter({
          slotManipulationState: {
            ...INIT_SLOT_MANIPULATION_STATE,
            dayCalendarColumns: clone?.dayCalendarColumns,
          },
        }),
      );
    }

    if (!!option) {
      clone.isLocking = option === SLOT_MANIPULATION_OPTIONS.LOCK_SLOT;
      clone.isCreating = option === SLOT_MANIPULATION_OPTIONS.CREATE_SLOT;
      clone.isAssigning = option === SLOT_MANIPULATION_OPTIONS.ASSIGN;

      if (!!clone.isLocking && !!clone.isCreating) {
        return dispatch(
          setSnackbar({
            message:
              "Error while changing internal state. Please contact support.",
            type: "error",
          }),
        );
      }
    }

    if (!!eventList) {
      clone.temporaryEvents = eventList;
    }

    if (!_.isEmpty(dayCalendarColumns)) {
      clone.dayCalendarColumns = dayCalendarColumns;
    }

    if (availableOptions) {
      clone.availableOptions = availableOptions;
    }

    if (reason && sessionId) {
      const tempEvents = _.cloneDeep(clone?.temporaryEvents);
      const found = tempEvents?.[sessionId];

      if (found) {
        const arr = found.map((sess) => ({
          ...sess,
          lockedReason: reason,
        }));

        clone.temporaryEvents = {
          ...tempEvents,
          [sessionId]: arr,
        };
      }
    }

    if (key && colIdx) {
      const tempEvents = _.cloneDeep(clone?.temporaryEvents);
      const found = tempEvents?.[colIdx];

      if (found) {
        const arr = changeBulkNewSlotKey({ key, value, sessionList: found });

        clone.temporaryEvents = {
          ...tempEvents,
          [colIdx]: arr,
        };
      }
    }

    dispatch(setter({ slotManipulationState: clone }));
  };

export const changeReviewFilters = (filter) => (dispatch, getState) => {
  if (_.isEmpty(filter)) {
    return false;
  }
  const currentContext = getState().reviews.activeState;
  const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);
  let filterClone = _.cloneDeep(
    getState().reviews[currentContext].filterObject,
  );

  for (let filterKey in filter) {
    if (filterClone[filterKey]) {
      filterClone[filterKey] = filter[filterKey];
    } else {
      filterClone = {
        ...INIT_FILTER_STATE,
        ...filterClone,
        [filterKey]: filter[filterKey],
      };
    }
  }

  dispatch(setter({ filterObject: filterClone }));
};

export const getEdReviewSessionsObject =
  (wardId, activeState = KIND_OF_REVIEW_STATE.ED_REVIEW) =>
  (dispatch, getState) => {
    let combinedId = wardId;
    const hospitalId = getState().ward.currentHospital;
    const isBarsActive = activeState === KIND_OF_REVIEW_STATE.BARS;

    if (getState().ward.isMultiHospitalMode && wardId === "ED") {
      combinedId = `${wardId}##${hospitalId}`;
    }

    if (isBarsActive) {
      const barsSessions =
        getState().reviews?.[KIND_OF_REVIEW_STATE.BARS].sessions;

      return {
        singleSessions: [],
        regularSessions: barsSessions,
      };
    }

    const statuses = getState()?.ward?.statuses?.[combinedId]
      ? getState()?.ward?.statuses?.[combinedId]
      : getState()?.ward?.statuses;

    return {
      singleSessions: statuses?.singleBedasSessions?.[wardId],
      regularSessions: statuses?.bedasList?.[wardId],
    };
  };

export const handleResetFilters = () => (dispatch, getState) => {
  const currentContext = getState().reviews.activeState;
  const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);

  dispatch(setter({ filterObject: INIT_FILTER_STATE }));
};

export const handleChangeSelectedReviewDate = (date) => (dispatch) => {
  dispatch(setSelectedDate(date));
};

export const handleOpenDailyCalendar = (open) => (dispatch) => {
  dispatch(setIsDailyCalendarOpen(open));
};

export const setSelectedPatient = (patient) => (dispatch, getState) => {
  const currentContext = getState().reviews.activeState;
  const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);

  dispatch(setter({ selectedPatient: patient }));
};

export const setHookSlotsObject = (slotObject) => (dispatch, getState) => {
  const currentContext = getState().reviews.activeState;
  const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);

  dispatch(setter({ hookSlots: slotObject }));
};

export const openReviewModal =
  (modal, helperState = null) =>
  (dispatch, getState) => {
    const currentStateClone = _.cloneDeep(getState().reviews.modalState);
    currentStateClone[modal] = true;

    if (helperState) {
      currentStateClone.modalHelperState = helperState;
    }

    dispatch(setModalState({ ...currentStateClone }));
  };

export const closeReviewModal = (modal) => (dispatch) => {
  dispatch(setModalState({ [modal]: false, modalHelperState: null }));
};

export const pushToTemporaryEventsList =
  (sessionId, eventItem, columnIndex) => (dispatch, getState) => {
    const currentContext = getState().reviews.activeState;
    const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);
    const stateClone = _.cloneDeep(
      getState().reviews?.[currentContext]?.slotManipulationState
        ?.temporaryEvents,
    );

    if (stateClone?.[sessionId] && sessionId !== "outsideSession") {
      if (
        stateClone[sessionId].findIndex(
          (ev) => ev?.startDate === eventItem?.startDate,
        ) === -1
      ) {
        stateClone[sessionId].push(eventItem);
        stateClone[sessionId] = _.sortBy(stateClone[sessionId], (ev) =>
          moment(ev?.startDate).format("HH:mm"),
        );
      }
    } else if (sessionId === "outsideSession") {
      stateClone[columnIndex] =
        _.isArray(stateClone?.[columnIndex]) &&
        stateClone?.[columnIndex].length > 0
          ? [...stateClone[columnIndex], eventItem]
          : [eventItem];
      // const currState = stateClone?.[columnIndex] || [];
      // // const currState = stateClone?.[columnIndex];
      //
      // console.log("sessionId ", sessionId, eventItem, columnIndex, currState);
      //
      // currState[sessionId] = _.isArray(currState?.[sessionId]) && currState?.[sessionId].length > 0 ?
      //   [...currState[sessionId], eventItem] :
      //   [eventItem];
      //
      // console.log("stateClone?.[columnIndex]",stateClone?.[columnIndex], stateClone);
    } else {
      stateClone[sessionId] = [eventItem];
    }

    dispatch(
      setter({
        slotManipulationState: {
          ...getState().reviews?.[currentContext]?.slotManipulationState,
          temporaryEvents: stateClone,
        },
      }),
    );
  };

export const removeFromTemporaryEventsList =
  (sessionId, eventItem) => (dispatch, getState) => {
    const currentContext = getState().reviews.activeState;
    const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);
    const stateClone = _.cloneDeep(
      getState().reviews?.[currentContext]?.slotManipulationState
        ?.temporaryEvents,
    );

    if (stateClone?.[sessionId] && sessionId !== "outsideSession") {
      const itemIndex = stateClone[sessionId].findIndex(
        (ev) =>
          ev.startDate ===
            (eventItem.startDate && ev.endDate === eventItem.endDate) ||
          eventItem?.id === ev?.id,
        // (eventItem?.id === ev?.id) ||
        // (eventItem?.sessionId === ev?.sessionId)
      );

      if (itemIndex !== -1) {
        stateClone[sessionId].splice(itemIndex, 1);

        if (
          _.isArray(stateClone?.[sessionId]) &&
          stateClone?.[sessionId].length === 0
        ) {
          delete stateClone[sessionId];
        }
      }
    } else if (sessionId === "outsideSession") {
      for (let sessId in stateClone) {
        const evList = stateClone[sessId];

      _.forEach(evList, (ev, idx) => {
        if (
          ev.sessionId === eventItem.sessionId &&
          ev.startDate === eventItem.startDate &&
          ev.endDate === eventItem.endDate
        ) {
          if (evList.length === 1) {
            delete stateClone[sessId];
          } else {
            evList.splice(idx, 1)
          }
        }
      })
    }
  } else {
    stateClone[sessionId] = [eventItem];
  }

    dispatch(
      setter({
        slotManipulationState: {
          ...getState().reviews?.[currentContext]?.slotManipulationState,
          temporaryEvents: stateClone,
        },
      }),
    );
  };

export const removeTemporaryEventsList =
  (sessionId, eventItem) => (dispatch, getState) => {
    const currentContext = getState().reviews.activeState;
    const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);
    const stateClone = _.cloneDeep(
      getState().reviews?.[currentContext]?.slotManipulationState
        ?.temporaryEvents,
    );

  if (_.isEmpty(eventItem)) {
    delete stateClone[sessionId];
  } else {
    stateClone[sessionId] = eventItem;
  }

    dispatch(
      setter({
        slotManipulationState: {
          ...getState().reviews[currentContext].slotManipulationState,
          temporaryEvents: stateClone,
        },
      }),
    );
  };

export const handleClickEditSessionButton =
  (session, sessionType) => (dispatch) => {
    dispatch(
      setModalHelperState({
        session,
        editMode: true,
        type: sessionType ? "single" : "regular",
      }),
    );
    dispatch(openReviewModal(ED_REVIEW_MODALS.ADD_EDIT_SESSION));
  };

// export const getPatientsAssignedToSession = (sessionId, parsedReviews, day) => (dispatch, getState) => {
//   const patientsToReview = _.cloneDeep(parsedReviews || getState().reviews.edReview.reviewList);
//
//   const predicate = (patient) => getReviewData(patient)?.assignedSession === sessionId && (
//     day ?
//       moment(patient?.bedas.date).format("DD/MM/YYYY") === `${day}` :
//       true
//   );
//
//   if (!_.isEmpty(patientsToReview)) {
//     return _.filter(patientsToReview, (patient) => {
//       return predicate(patient);
//     });
//   }
//   return [];
// }

const fetchAllReviewsGenerator =
  (wardIds, bedasStatusArray) => (dispatch, getState) => {
    const getReqBody = (status) => {
      const body = {
        wardIds,
        bedasStatus: status,
      };
      return body;
    };

    const promises = bedasStatusArray.map((status) => {
      return (
        _.isString(status) &&
        API.findPatientsInWard(getReqBody(status))
          .then((res) => {
            if (!_.isEmpty(res?.data?.__data)) {
              return res?.data?.__data;
            }
          })
          .catch((err) => {
            console.error(err);
            dispatch(
              setSnackbar({
                type: "error",
                message: `System could not fetch ${status} patients to be reviewed. Please try again later.`,
              }),
            );
          })
      );
    });

    return Promise.all(promises);
  };

export const fetchPatientsToBeReviewed =
  ({ hospitalId, statusesArray, initiallyDone }) =>
  (dispatch, getState) => {
    let wardIds = [];
    const isBars = getState().reviews.activeState === KIND_OF_REVIEW_STATE.BARS;
    const statuses = (statusesArray || ["started", "inReview"]).filter(
      (status) => status !== "notAssigned",
    );

    if (config.modules?.map?.customMap) {
      if (!hospitalId) hospitalId = getState().ward.currentHospital;
      wardIds.push(`ED##${hospitalId}`);
    } else wardIds = ["ED", "0EDAU"];

    dispatch(fetchAllReviewsGenerator(wardIds, statuses, initiallyDone)).then(
      (res) => {
        if (_.isEmpty(res) || !res) return;

        let validReviews = [];
        let cancelledReviews = [];

        _.forEach(res, (reviews) => {
          if (!!reviews) {
            if (reviews?.[0]?.bedas?.status === "cancelled") {
              cancelledReviews = _.concat(cancelledReviews, reviews);
            } else {
              validReviews = _.concat(validReviews, reviews);
            }
          }
        });

        dispatch(setReviewList(validReviews));

        if (!_.isEmpty(cancelledReviews)) {
          dispatch(setReviewList(cancelledReviews, true));
        }
      },
    );
  };

// @param type: regular | single
export const getEdReviewSessionDetails =
  ({ sessionId, type, kindOfActiveEvents }) =>
  (dispatch, getState) => {
    const eventKind = !kindOfActiveEvents
      ? KIND_OF_REVIEW_STATE.ED_REVIEW
      : kindOfActiveEvents;
    const sessionCache = getState().reviews?.[eventKind].sessionCache;
    const sessionKind = type === "regular" ? "regular" : "single";
    const isBars = getState().reviews.activeState === KIND_OF_REVIEW_STATE.BARS;

    if (sessionCache?.[sessionKind]?.[sessionId]) {
      return sessionCache?.[sessionKind]?.[sessionId];
    } else {
      let session = dispatch(
        getSessionEvent({ sessionId, sessionKind, isBars }),
      );

      if (!session) {
        session = dispatch(
          getSessionEvent({
            sessionId,
            sessionKind: type === "regular" ? "single" : "regular",
            isBars,
          }),
        );
      }

      if (session) {
        dispatch(
          setSessionCache({
            type: sessionKind,
            sessionId,
            data: session,
            contextType: eventKind,
          }),
        );
        return session;
      }
    }
  };

export const cacheDaySessions =
  ({ sessions }) =>
  (dispatch) => {
    const reviews = [];

    if (!_.isEmpty(sessions)) {
      for (let regularSessionId of sessions.regular) {
        reviews.push(
          dispatch(
            getEdReviewSessionDetails({
              sessionId: regularSessionId,
              type: "regular",
            }),
          ),
        );
      }

      for (let singleSessionId of sessions.single) {
        reviews.push(
          dispatch(
            getEdReviewSessionDetails({
              sessionId: singleSessionId,
              type: "single",
            }),
          ),
        );
      }
    }

    return reviews;
  };

export const getSessionEvent =
  ({ wardId = "ED", sessionId = "", sessionKind, isBars }) =>
  (dispatch, getState) => {
    if (isBars) {
      return _.find(
        getState()?.reviews?.[KIND_OF_REVIEW_STATE.BARS].sessions,
        (session) =>
          session?.sessionId
            ? session?.sessionId === sessionId
            : session?.__id === sessionId,
      );
    } else {
      const hospitalId = getState().ward.currentHospital;
      const combinedId = `${wardId}##${hospitalId}`;
      const wardSliceKey = getWardDataAppointmentsKey(
        isBars,
        sessionKind === "single",
      );

      if (!sessionId) {
        return getState()?.ward?.statuses?.[combinedId]?.[wardSliceKey]?.[
          wardId
        ];
      }

      return _.find(
        getState()?.ward?.statuses?.[combinedId]?.[wardSliceKey]?.[wardId],
        (session) =>
          session?.sessionId
            ? session?.sessionId === sessionId
            : session?.id === sessionId,
      );
    }
  };

// @Month -> string 'MM/YYYY'
// @Day -> string 'D'
export const retrieveEventsOnDate =
  ({ month, day = null, wardId = "ED", onlyIds = true, eventsState }) =>
  (dispatch, getState) => {
    const activeCtxType = isValidContextType(eventsState)
      ? eventsState
      : KIND_OF_REVIEW_STATE.ED_REVIEW;
    const edReviewState = getState().reviews?.[activeCtxType];
    const sessionsOnMonth = edReviewState?.sessionIds?.[month] || {};

    if (onlyIds) {
      return !day ? sessionsOnMonth : sessionsOnMonth?.[day] || {};
    } else {
      const sessions = [];

      if (!day) {
        return null;
      }

      return sessions;
    }
  };

export const retrieveReviewSlotsOnDay =
  ({ day, cancelled = false, sessionId = "", kindOfActiveEvents }) =>
  (dispatch, getState) => {
    const reviewsClone = _.cloneDeep(
      getState().reviews[kindOfActiveEvents]?.reviewList,
    );

    return filterReviewsByDayAndSessionId(reviewsClone, {
      day,
      cancelled,
      sessionId,
      bars: kindOfActiveEvents === KIND_OF_REVIEW_STATE.BARS,
    });
  };

export const getPatientReviewStatus =
  (patientNumber, patient) => (dispatch, getState) => {
    const patientsOnWb = getState().ward.patients;
    // const searchedPatient = patient || _.find(
    const searchedPatient = _.find(
      patientsOnWb,
      (pt) =>
        patient
          ? patient?.patient?.hospitalNumber === patientNumber
          : pt?.patient?.hospitalNumber === patientNumber,
      0,
    );

    return getPatientsReviewStatus(searchedPatient);
  };

export const modifyReview =
  ({
    modifiedValuesObject,
    key,
    originalObject,
    patientId,
    onAfterSave,
    onError,
  }) =>
  (dispatch, getState) => {
    if (!patientId || !key) {
      dispatch(
        setSnackbar({
          type: "error",
          message: "Cannot receive all necessary patient's data.",
        }),
      );
      return onError && onError();
    }

    dispatch(setUpdating(true));

    const [modifiedValues, dateValues] = getPreparedReviewObject({
      originalObject,
      objectKey: key,
      modifiedValues: modifiedValuesObject,
    });

    API.updatePatientInWard(patientId, modifiedValues, dateValues)
      .then((response) => {
        if (response.status === 200 && response.data === true) {
          dispatch(
            setSnackbar({
              type: "success",
              message: "Patient has been modified successfully.",
            }),
          );
          dispatch(fetchPatientsToBeReviewed(getState().ward.currentHospital));
        } else {
          console.warn(
            "Error occured. Patient has not been modified. Please try again later.",
          );
          dispatch(
            setSnackbar({
              type: "error",
              message: "ED Review has not been modified.",
            }),
          );
        }
      })
      .finally(() => {
        dispatch(setUpdating(false));

        if (_.isFunction(onAfterSave)) {
          return onAfterSave();
        }
        return modifiedValues;
      });
  };

export const buildCalendarObject =
  ({
    sessions,
    wardId = "ED",
    filterObject = null,
    currentCalendar = null,
    forceRefresh = false,
  }) =>
  (dispatch, getState) => {
    let regularSessions, singleSessions, calendar;

    const currentContext = getState().reviews.activeState;
    const isBars = currentContext === KIND_OF_REVIEW_STATE.BARS;
    const currentActiveState = getState().reviews[currentContext];
    const setter = getStateSetter(currentContext === KIND_OF_REVIEW_STATE.BARS);

    if (!_.isEmpty(sessions) || sessions?.regularSessions) {
      regularSessions = sessions?.regularSessions || null;
      singleSessions = sessions?.singleSessions || null;
    } else {
      regularSessions = dispatch(
        getSessionEvent({
          wardId: wardId || getState().reviews?.selectedWardId,
          sessionKind: "regular",
          isBars,
        }),
      );

      singleSessions = dispatch(
        getSessionEvent({
          wardId: wardId || getState().reviews?.selectedWardId,
          sessionKind: "single",
          isBars,
        }),
      );
    }

    if (currentCalendar && forceRefresh) {
      calendar = currentCalendar;
    } else {
      calendar = currentActiveState?.sessionIds || {};
    }
    const builtCalendar = prepareSessionsForCalendarObject({
      filterObject,
      forceRefresh,
      currentCalendar: calendar,
      sessions: { regularSessions, singleSessions },
    });

    dispatch(
      setter({
        sessionIds: builtCalendar,
      }),
    );

    return builtCalendar;
  };

export const updateSingleSessions =
  (hospitalIdd, data, afterSaveEvent, hospital, isDelete = false) =>
  async (dispatch, getState) => {
    const hospitalId = getState().ward.currentHospital;
    const combinedId = `${hospital.hospitalId}##${hospitalId}`;
    const statuses = getState()?.ward?.statuses?.[combinedId]
      ? getState()?.ward?.statuses?.[combinedId]
      : getState()?.ward?.statuses;

    const isBars = data?.isBars;
    let sessionsClone = _.cloneDeep(data);
    const appointmentTypeSnackbarString = isBars
      ? "BaRS Appointment"
      : "Appointment";
    const wardAppointmentsKey = getWardDataAppointmentsKey(isBars, true);

    let sessionsFromSlice = _.cloneDeep(
      statuses?.[wardAppointmentsKey]?.[hospital?.hospitalId] || [],
    );

    // Look if there is any session in data array and iterate through our slice state,
    // get this item and remove because this object in data object will overwrite current state with newer state
    if (!_.isEmpty(sessionsFromSlice)) {
      if (_.isArray(data)) {
        _.forEach(data, (sess) => {
          const sliceIndex = sessionsFromSlice.findIndex(
            (item) => item?.startDate === sess?.startDate,
          );

          if (sliceIndex >= 0) {
            sessionsFromSlice.splice(sliceIndex, 1);
          }
        });
      }

      if (isDelete) {
        _.remove(sessionsFromSlice, { sessionId: data.sessionId });
      }
    }

    if (isDelete) {
      sessionsClone = sessionsFromSlice;
    } else {
      sessionsClone = _.concat(sessionsClone, sessionsFromSlice);
    }

    // Sort events by start date and remove older than today
    if (!_.isEmpty(sessionsClone)) {
      sessionsClone = _.sortBy(sessionsClone, ["startDate"]);
      sessionsClone = _.filter(sessionsClone, (session) =>
        moment(session?.startDate).isSameOrAfter(
          moment().set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
        ),
      );
    }

    const combinedSessions = {
      ...statuses?.[wardAppointmentsKey],
      [hospital?.hospitalId]: sessionsClone,
      modifiedDate: moment().toISOString(),
    };

    let ward = "ED";
    if (hospitalId) ward += encodeURIComponent(`##${hospitalId}`); // For UHD

    try {
      const response = await API.updateWardData(ward, [
        { [wardAppointmentsKey]: combinedSessions },
      ]);
      if (response.data) {
        dispatch(
          setSnackbar({
            type: "success",
            message: `${appointmentTypeSnackbarString} Session was successfully saved.`,
          }),
        );
        dispatch(setSingleBedasSessions(combinedSessions));
      }
    } catch (e) {
      dispatch(
        setSnackbar({
          type: "error",
          message: `${appointmentTypeSnackbarString} Session not saved. Error occurred!`,
        }),
      );
      console.error("error in network", e);
    } finally {
      if (afterSaveEvent) {
        afterSaveEvent();
      }
    }
  };

export const modifyRegularEvents =
  (hospitalId, data, afterSaveEvent, hospital, mode = "create") =>
  async (dispatch, getState) => {
    const isDelete = mode === "delete";
    const isUpdate = mode === "update";

    const isBars = data?.isBars;
    const appointmentTypeSnackbarString = isBars
      ? "BaRS Appointment"
      : "Appointment";
    const wardAppointmentsKey = getWardDataAppointmentsKey(isBars, false);

    const hospitalId = getState().ward.currentHospital;
    const combinedId = `${hospital.hospitalId}##${hospitalId}`;
    const statuses = getState()?.ward?.statuses?.[combinedId]
      ? getState()?.ward?.statuses?.[combinedId]
      : getState()?.ward?.statuses;

    const allRegularSessions = _.cloneDeep(statuses?.[wardAppointmentsKey]);
    let sessionsFromSlice = _.cloneDeep(
      statuses?.[wardAppointmentsKey]?.[hospital?.hospitalId] || [],
    );

    if (isDelete) {
      _.remove(sessionsFromSlice, { sessionId: data.sessionId });
    } else if (isUpdate) {
      const index = _.findIndex(sessionsFromSlice, {
        sessionId: data.sessionId,
      });

      if (index === -1) {
        return dispatch(
          setSnackbar({
            type: "error",
            message: `${appointmentTypeSnackbarString} Session not saved. Not found session with given ID!`,
          }),
        );
      } else {
        sessionsFromSlice[index] = data;
      }
    } else {
      if (_.isArray(data)) {
        sessionsFromSlice.push(...data);
      } else {
        sessionsFromSlice.push(data);
      }
    }

    if (!_.isEmpty(sessionsFromSlice)) {
      sessionsFromSlice = _.sortBy(sessionsFromSlice, ["startDate"]);
    }

    const combinedState = {
      ...allRegularSessions,
      [hospital?.hospitalId]: sessionsFromSlice,
      modifiedDate: moment().toISOString(),
    };

    let ward = "ED";
    if (hospitalId) ward += encodeURIComponent(`##${hospitalId}`); // For UHD

    try {
      const response = await API.updateWardData(ward, [
        { [wardAppointmentsKey]: combinedState },
      ]);

      if (response.data) {
        dispatch(
          setSnackbar({
            type: "success",
            message: `${appointmentTypeSnackbarString} Session was successfully saved.`,
          }),
        );
        dispatch(setBedasList(combinedState));
      }
    } catch (e) {
      dispatch(
        setSnackbar({
          type: "error",
          message: `${appointmentTypeSnackbarString} Session not saved. Error occured!`,
        }),
      );
      console.error("error in network", e);
    } finally {
      if (afterSaveEvent) {
        afterSaveEvent();
      }
    }
  };

export const selectPatientForAppointment =
  (patient, fetchPatientInfo = false) =>
  async (dispatch, getState) => {
    if (!patient?.__id) {
      return dispatch(
        setSnackbar({
          type: "error",
          message:
            "Patient is not valid. Please try again later or contact support.",
        }),
      );
    }

    try {
      let pt = patient;

      dispatch(setLoading(true));

      if (!!fetchPatientInfo) {
        const apiResponse = await API.getPatientAttendancesHistory(pt?.__id);

        if (!_.isEmpty(apiResponse)) {
          pt = apiResponse?.[0];
        } else {
          dispatch(
            setSnackbar({
              type: "error",
              message:
                "Cannot get patient history. Please admit patient first.",
            }),
          );
        }
      }

      const currentModalHelperState =
        getState().reviews.modalState.modalHelperState;

      dispatch(
        setModalHelperState({
          ...currentModalHelperState,
          selectedPatient: pt,
        }),
      );
    } catch (e) {
      dispatch(
        setSnackbar({
          type: "error",
          message: "Error selecting patient. Please try again later.",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const createMultipleBarsSession =
  (session, appointmentMode, onAfterSave) => async (dispatch, getState) => {
    const multipleBars = createMultipleSessions(session, appointmentMode);
    const clone = _.cloneDeep(
      getState().reviews?.[KIND_OF_REVIEW_STATE.BARS].sessions,
    );

    try {
      if (!_.isEmpty(multipleBars)) {
        const results = await Promise.all(
          multipleBars.map((bar) => API.addBarsSession(bar)),
        );

        if (!_.isEmpty(results)) {
          _.forEach(results, (result) => {
            if (result?.__id) {
              clone.push(result);
            }
          });

          dispatch(
            setSnackbar({
              type: "success",
              message:
                "All BaRS Appointment Sessions have been successfully created.",
            }),
          );

          dispatch(setBarsReview({ sessions: clone }));
        }

        onAfterSave();
      } else {
        return dispatch(
          setSnackbar({
            type: "error",
            message: "Please provide valid session options in form.",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar({
          type: "error",
          message:
            "Error during BaRS Appointment Sessions creation. Please try again later.",
        }),
      );
    }
  };

export const createBarsSession =
  (sessionObj, onAfterSave) => async (dispatch, getState) => {
    const session = createBarsSessionObject(sessionObj);

    try {
      const apiResponse = await API.addBarsSession(session);

      if (apiResponse && apiResponse.__id) {
        onAfterSave();

        const clone = _.cloneDeep(
          getState().reviews?.[KIND_OF_REVIEW_STATE.BARS].sessions,
        );

        clone.push(apiResponse);
        dispatch(setBarsReview({ sessions: clone }));
        dispatch(
          setSnackbar({
            type: "success",
            message: "BaRS Appointment Session has been successfully created.",
          }),
        );
      }
    } catch (e) {
      console.error(
        "Error creating BaRS Session. Please try again later or contact support.",
      );
      dispatch(
        setSnackbar({
          type: "error",
          message:
            "Error creating BaRS Session. Please try again later or contact support.",
        }),
      );
    }
  };

export const fetchBarsSessions =
  (setSlice = false) =>
  async (dispatch) => {
    try {
      const apiResponse = await API.getBarsSessions();

      if (apiResponse) {
        if (setSlice) {
          dispatch(setBarsReview({ sessions: apiResponse }));
        }

        return apiResponse;
      }
    } catch (e) {
      dispatch(
        setSnackbar({
          type: "error",
          message:
            "Error fetching BaRS Sessions. Please try again or contact support.",
        }),
      );
      console.error(`Error fetching BaRS Sessions: `, e);
    }
  };

export const deleteBarsSession = (session) => async (dispatch) => {
  const dbId = session?.__id;
  if (!dbId) {
    console.error(`Error deleting BaRS Session: ${session}`);
    return dispatch(
      setSnackbar({
        type: "error",
        message:
          "Error during BaRS Session deletion. Please provide valid session id.",
      }),
    );
  }

  try {
    const apiResponse = await API.deleteBarsSession(dbId);

    if (apiResponse === false) {
      dispatch(
        setSnackbar({
          type: "error",
          message: `Session ${session?.sessionName} could not be removed. Please reschedule assigned patients or contact support.`,
        }),
      );
      return;
    }
  } catch (e) {
    dispatch(
      setSnackbar({
        type: "error",
        message:
          "Error during BaRS Session deletion. Please try again or contact support.",
      }),
    );
    console.error(`Error during BaRS Session deletion: `, e);
  }
};

export default reviewSlice.reducer;
